import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Grid, Tab, Alert, AlertTitle, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import {
  TabContext, TabList, TabPanel,
} from '@mui/lab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-regular-svg-icons';
import { NavLink } from 'react-router-dom';
import { CommandPanels, EditorControls, withBaseEditor } from '../../BaseEditor';
import Attributes from './attributes';
import Notes from './notes';
import { AppContext } from '../../../providers/authProvider';
import api from '../../../api/req';
import PayerRealies from './realties';
import PayerLands from './lands';
import PayerContracts from './contracts';
import TaxgroupHistoryTablePart from './tp/taxgrouphistory';

function EditorRenderer({
  getControl,
  data,
  dataSetter,
  currentUser,
  erroredFields,
  fields,
}) {
  const [bdate, setBDate] = useState(null);
  const [edate, setEDate] = useState(null);
  const [kdb, setKdb] = useState(null);
  const [notesList, setNotesList] = useState(null);
  const [err, setErr] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditCode, setEditCode] = useState(true);
  const [isOpenQs, setOpenQs] = React.useState(false);
  const [personId, setPersonId] = React.useState('create');

  const { auth } = useContext(AppContext);

  const handleClickOpenQs = () => {
    setOpenQs(true);
    setEditCode(true);
  };

  const handleCloseQs = (isEdit) => {
    setOpenQs(false);
    setEditCode(isEdit);
  };

  const getNotes = useCallback((e = null) => {
    if (e) e.preventDefault();
    const notes = async () => {
      setErr(null);
      setIsLoading(true);
      const params = {
        bdate,
        edate,
      };
      if (kdb) {
        params.kdb_id = kdb.id;
      }
      const r = await api.post$(`/api/catalog/payers/${data.id}/get_notes/`, auth, { ...params });
      if (r.ok) {
        const d = await r.json();
        setNotesList(d);
        setIsLoading(false);
      } else {
        setErr(setErr);
        setIsLoading(false);
      }
    };
    notes();
  }, [auth, bdate, data.id, edate, kdb]);

  const getPersonId = useCallback(() => {
    const person = async () => {
      setErr(null);
      setIsLoading(true);

      const r = await api.get$(`/api/catalog/payers/${data.id}/get_id_person/`, auth);
      if (r.ok) {
        const d = await r.json();
        setPersonId(d.person_id);
        setIsLoading(false);
      } else {
        setErr(setErr);
        setIsLoading(false);
      }
    };
    person();
  }, [auth, data.id]);

  useEffect(
    () => {
      getNotes();
      getPersonId();
    },
    [getNotes, getPersonId],
  );
  const [tab, setTab] = useState('general');

  const {
    attributes,
    attributes_list: attributesList,
  } = data;

  const onTaxgroupChange = useCallback(
    (e, newRecs) => dataSetter({ taxgrouphistory_set: newRecs }),
    [dataSetter],
  );

  return (
    <TabContext value={tab}>
      <TabList
        onChange={(e, v) => setTab(v)}
        textColor="primary"
        indicatorColor="primary"
        variant="scrollable"
      >
        <Tab value="general" label="Загальне" />
        <Tab value="tax_group" label="Податкові реквізити" />
        <Tab value="notes" label="Платежі" />
        <Tab value="contacts" label="Контактні дані" />
        <Tab value="economics" label="Економічні показники" />
        <Tab value="contracts" label="Договори" disabled={!data.id} />
        <Tab value="realties" label="Нерухомість" disabled={!data.id} />
        <Tab value="lands" label="Земельні ділянки" disabled={!data.id} />
        <Tab value="acts" label="Дані для актів" />
        <Tab value="attrs" label="Додаткові властивості" />
      </TabList>
      <TabPanel value="general">
        <Grid container spacing={2}>
          <Grid item sm={3} display="flex">
            {getControl('code', {
              disabled: isEditCode,
            })}
            <Button
              startIcon={<FontAwesomeIcon icon={faEdit} />}
              onClick={() => {
                setEditCode(!isEditCode);
                if (isEditCode) handleClickOpenQs();
              }}
              size="small"
            />

            <Dialog
              open={isOpenQs}
              onClose={handleCloseQs}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title" textAlign="center">
                <>
                  <p>Заміна коду веде до непередбачуваних наслідків! </p>
                  Дозволити редагувати код Платника податку?
                </>

              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  Для зміни коду треба мати вагомі підстави,
                  оскільки пошук Платника податку в казначейських виписках здійснюється саме по коду.
                  Зміна коду може призвести до того, що система не зможе правильно визначити Платника податку.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => handleCloseQs(true)} autoFocus>Ні</Button>
                <Button onClick={() => handleCloseQs(false)}>
                  Редагувати
                </Button>
              </DialogActions>
            </Dialog>

          </Grid>
          <Grid item sm={7}>
            {getControl('name', {
              onChange: (e, v) => {
                if (!data.full_name || data.name === data.full_name) {
                  dataSetter({
                    name: v,
                    full_name: v,
                  });
                } else {
                  dataSetter({ name: v });
                }
              },
            })}
          </Grid>
          <Grid item sm={2} display="flex" justifyContent="end">
            <NavLink to={`/cat/householdMembers/${personId}/${(personId === 'create') ? data.id : ''}`}>
              <Button
                color="primary"
                size="small"
                variant="text"
              >
                Переглянути дані фізичної особи
              </Button>
            </NavLink>
          </Grid>
          {data.last_note && (
            <Grid item sm={12}>
              <Alert
                severity="info"
                variant="outlined"
                action={(
                  <Button color="primary" size="small" variant="text" onClick={() => setTab('notes')}>
                    Переглянути всі платежі
                  </Button>
                )}
              >
                <AlertTitle>Текст останнього платежу</AlertTitle>
                {data.last_note}
              </Alert>
            </Grid>
          )}
          <Grid item sm={12}>
            <EditorControls.TextInput
              value={data.full_name}
              onChange={(e, v) => dataSetter({ full_name: v })}
              label={fields.full_name.label}
              errors={erroredFields.full_name}
            />
          </Grid>
          <Grid item sm={6}>
            {getControl('industry', {
              canErase: true,
              backend: '/api/catalog/industries/',
              filter: {
                org: currentUser.is_superuser ? data.org && data.org.id : currentUser.org.id,
              },
            })}
          </Grid>
          <Grid item sm={6}>
            {getControl('region', {
              canErase: true,
              backend: '/api/catalog/regions/',
              filter: {
                org: currentUser.is_superuser ? data.org && data.org.id : currentUser.org.id,
              },
            })}
          </Grid>
          <Grid item sm={(data.payer_category === 3) ? 12 : 4}>
            {getControl('payer_category')}
            {getControl('is_bank')}
            {getControl('is_landlord')}
          </Grid>
          {data.payer_category && (data.payer_category !== 3) && (
            <>
              <Grid item sm={3}>
                {getControl('date_of_birth')}
              </Grid>
              <Grid item sm={3}>
                {getControl('sex')}
              </Grid>
              <Grid item sm={3}>
                {getControl('code_is_passport')}
              </Grid>
            </>
          )}
          {currentUser.is_superuser && (
            <Grid item sm={12}>
              {getControl('org', {
                backend: '/api/catalog/orgs/',
              })}
            </Grid>
          )}
          <Grid item sm={12}>
            <EditorControls.TextInput
              value={data.comment}
              onChange={(e, v) => dataSetter({ comment: v })}
              label="Коментар"
            />
          </Grid>
        </Grid>
      </TabPanel>
      <TabPanel value="tax_group">
        <Typography
          variant="h4"
          color="primary"
        >
          {fields.taxgrouphistory_set.label}
        </Typography>
        <TaxgroupHistoryTablePart
          data={data.taxgrouphistory_set}
          options={fields.taxgrouphistory_set}
          onChange={onTaxgroupChange}
          errors={erroredFields.taxgrouphistory_set}
        />
      </TabPanel>
      <TabPanel value="contacts">
        <Grid container spacing={2}>
          <Grid item sm={6}>
            {getControl('phones')}
          </Grid>
          <Grid item sm={6}>
            {getControl('email')}
          </Grid>
          <Grid item sm={12}>
            {getControl('locality')}
          </Grid>
          <Grid item sm={12}>
            <EditorControls.TextInput
              value={data.address}
              onChange={(e, v) => dataSetter({ address: v })}
              label={fields.address.label}
              errors={erroredFields.address}
            />
          </Grid>
        </Grid>
      </TabPanel>
      <TabPanel value="economics">
        <Grid container spacing={2}>
          <Grid item sm={6}>
            {getControl('kved', {
              backend: '/api/catalog/kveds/',
            })}
          </Grid>
          <Grid item sm={6}>
            {getControl('registration_date')}
          </Grid>
          <Grid item sm={12}>
            <EditorControls.MultiItemSelect
              value={data.kved_additional}
              onChange={(e, v) => dataSetter({ kved_additional: v })}
              backend="/api/catalog/kveds/"
              label="Додаткові КВЕД"
            />
          </Grid>
          {!data.individual && (
            <>
              <Grid item sm={6}>
                {getControl('status')}
              </Grid>
              <Grid item sm={6}>
                {getControl('boss')}
              </Grid>
              <Grid item sm={6}>
                {getControl('staff')}
              </Grid>
              <Grid item sm={6}>
                {getControl('type_opf')}
              </Grid>
              <Grid item sm={12}>
                {getControl('type_by_size')}
              </Grid>
            </>
          )}
          <Grid item sm={6}>
            {getControl('all_land')}
          </Grid>
          <Grid item sm={6}>
            {getControl('all_realty')}
          </Grid>
        </Grid>
      </TabPanel>
      <TabPanel value="acts">
        <Grid container spacing={2}>
          <Grid item sm={12}>
            <EditorControls.TextInput
              value={data.name_of_position}
              onChange={(e, v) => dataSetter({ name_of_position: v })}
              label="Повне ім'я для акту"
            />
          </Grid>
          <Grid item sm={12}>
            {getControl('position')}
          </Grid>
        </Grid>
      </TabPanel>
      <TabPanel value="contracts">
        <PayerContracts payerId={data.id} />
      </TabPanel>
      <TabPanel value="realties">
        <PayerRealies payerId={data.id} />
      </TabPanel>
      <TabPanel value="lands">
        <PayerLands payerId={data.id} />
      </TabPanel>
      <TabPanel value="notes">
        <Notes
          bdate={bdate}
          setBDate={setBDate}
          edate={edate}
          setEDate={setEDate}
          kdb={kdb}
          setKdb={setKdb}
          notesList={notesList}
          getNotes={getNotes}
          err={err}
          isLoading={isLoading}
        />
      </TabPanel>
      <TabPanel value="attrs">
        <Attributes
          attributes={attributes}
          dataSetter={dataSetter}
          attributesList={attributesList}
        />
      </TabPanel>
    </TabContext>
  );
}

EditorRenderer.propTypes = {
  getControl: PropTypes.func.isRequired,
  data: PropTypes.shape().isRequired,
  dataSetter: PropTypes.func.isRequired,
  currentUser: PropTypes.shape().isRequired,
  erroredFields: PropTypes.shape({
    full_name: PropTypes.arrayOf(PropTypes.string),
    address: PropTypes.arrayOf(PropTypes.string),
    taxgrouphistory_set: PropTypes.arrayOf(PropTypes.string),
  }),
  fields: PropTypes.shape({
    full_name: PropTypes.shape({
      label: PropTypes.string,
    }),
    address: PropTypes.shape({
      label: PropTypes.string,
    }),
    taxgrouphistory_set: PropTypes.shape({
      label: PropTypes.string,
    }),
  }).isRequired,
};

EditorRenderer.defaultProps = {
  erroredFields: {
  },
};

export default withBaseEditor(EditorRenderer, '/api/catalog/payers/', CommandPanels.catCommandPanel);
